import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Owns the autopdf job's server state and the derived "render"-related
 * client state (pdfUrl, hasAnswers, isFinalizedView). Exposes the fetch
 * and polling primitives so the page can drive Generate/Finalize/Download.
 *
 * State surface intentionally stays mutable from the outside (`setJob`,
 * `setPdfUrl`, etc.) — the page still owns the workflow handlers; this
 * hook just consolidates the data layer.
 */
export default function useAutoPdfJob(jobId, initialPdfUrl) {
  const [job, setJob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(initialPdfUrl || "");
  const [loading, setLoading] = useState(true);
  const [hasAnswers, setHasAnswers] = useState(false);
  const [isFinalizedView, setIsFinalizedView] = useState(false);

  // PDF-rendering state — kept here because applyJobData has to reset
  // pageDims/totalPages whenever it swaps pdfUrl.
  const [totalPages, setTotalPages] = useState(0);
  const [pageDims, setPageDims] = useState({});

  // Latest pdfUrl ref so applyJobData can compare without recreating itself
  // every render.
  const pdfUrlRef = useRef(pdfUrl);
  useEffect(() => {
    pdfUrlRef.current = pdfUrl;
  }, [pdfUrl]);

  const applyJobData = useCallback((data) => {
    setJob(data);

    // Manual jobs are always editable: even when a filled PDF exists (from a
    // prior download), reopening should land on the SOURCE PDF with the
    // editable overlays — not the read-only baked output. AI jobs prefer the
    // filled output so back-navigation shows the finalized result.
    const isManual = data?.mode === "manual";
    const preferredUrl = isManual
      ? data?.pdf_url || data?.filled_pdf_url || ""
      : data?.filled_pdf_url || data?.pdf_url || "";
    if (preferredUrl && preferredUrl !== pdfUrlRef.current) {
      setPageDims({});
      setTotalPages(0);
      setPdfUrl(preferredUrl);
    }

    setIsFinalizedView(!isManual && Boolean(data?.filled_pdf_url));

    const anyAnswered = (data?.fields || []).some(
      (f) => f.answer && f.answer.toString().trim() !== ""
    );
    setHasAnswers(anyAnswered);
  }, []);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await api.get(`/api/v1/rfp/autopdf/jobs/${jobId}/`);
      if (res.status === 200) {
        applyJobData(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch job:", err);
      toast.error("Failed to load form fields.");
    } finally {
      setLoading(false);
    }
  }, [jobId, applyJobData]);

  /**
   * Generic poll: GET /jobs/<id>/ on an interval until `isDone(data)` returns
   * truthy. Updates the live job state on every tick so the UI reflects
   * background-task progress (fields appearing, answers filling in, etc.).
   */
  const pollJobUntil = useCallback(
    async (
      isDone,
      { intervalMs = 2000, timeoutMs = 5 * 60 * 1000, timeoutMsg, isCancelled } = {}
    ) => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        if (isCancelled?.()) return { cancelled: true };
        const res = await api.get(`/api/v1/rfp/autopdf/jobs/${jobId}/`);
        if (res.status === 200) {
          const data = res.data;
          applyJobData(data);
          if (data?.error_message) throw new Error(data.error_message);
          if (isDone(data)) return data;
        }
        if (isCancelled?.()) return { cancelled: true };
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
      throw new Error(timeoutMsg || "Timed out waiting for the job to update.");
    },
    [jobId, applyJobData]
  );

  /**
   * Poll until the async fill task finishes (status leaves "filling"). Bboxes
   * light up with answers as soon as the backend exposes them.
   */
  const pollJobUntilFilled = useCallback(
    (opts = {}) =>
      pollJobUntil((data) => {
        const status = (data?.status || "").toLowerCase();
        return status && status !== "filling";
      }, { timeoutMsg: "Timed out waiting for answers to be generated.", ...opts }),
    [pollJobUntil]
  );

  /**
   * Poll until the AI annotation task finishes (status leaves
   * "extracting_fields"). Returns once the job is ready for review.
   */
  const pollJobUntilDetected = useCallback(
    (opts = {}) =>
      pollJobUntil((data) => {
        const status = (data?.status || "").toLowerCase();
        return status && status !== "extracting_fields";
      }, { timeoutMsg: "Timed out waiting for AI to annotate the form.", ...opts }),
    [pollJobUntil]
  );

  useEffect(() => {
    if (jobId) fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  return {
    job,
    setJob,
    pdfUrl,
    setPdfUrl,
    loading,
    setLoading,
    hasAnswers,
    setHasAnswers,
    isFinalizedView,
    setIsFinalizedView,
    totalPages,
    setTotalPages,
    pageDims,
    setPageDims,
    applyJobData,
    fetchJob,
    pollJobUntil,
    pollJobUntilFilled,
    pollJobUntilDetected,
  };
}
